/**
 * SaveMatterNoteModal — saves a Claude AI analysis as a structured matter note.
 * Calls POST /api/hub/matter-notes which stores in MongoDB + files to SharePoint Attendances.
 */

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, FileText, ExternalLink } from "lucide-react";
import axios from "axios";
import { API } from "../config/api";
import SmartClientMatterSearch from "./SmartClientMatterSearch";
import { RecordTimeField, EMPTY_TIME, recordTouchpointTime, timeFieldProblem } from "./time/RecordTimeField";

import { errorMessage } from "../utils/errorMessage";
const NOTE_TYPE_OPTIONS = [
  { value: "contract_review_client_perspective", label: "Contract review – client perspective" },
  { value: "contract_review_other_side_amends", label: "Contract review – other side's amendments" },
  { value: "hots_review", label: "Heads of terms review" },
  { value: "other_side_arguments", label: "Other side's arguments / case analysis" },
  { value: "chronology", label: "Chronology / matter events" },
  { value: "general", label: "General note" },
];

const DEFAULT_TITLES = {
  contract_review_client_perspective: "Contract review – client perspective",
  contract_review_other_side_amends: "Contract review – other side's amendments",
  hots_review: "Heads of terms review",
  other_side_arguments: "Case analysis",
  chronology: "Chronology",
  general: "General note",
};

// BM-H4112: standard invoice line per note type. Never the note itself,
// which is what the client would otherwise read on the bill.
const TIME_NARRATIVES = {
  contract_review_client_perspective: "Reviewing contract",
  contract_review_other_side_amends: "Considering other side's amendments to contract",
  hots_review: "Reviewing heads of terms",
  other_side_arguments: "Considering other side's case",
  chronology: "Preparing chronology",
  general: "Attendance on file",
};

export default function SaveMatterNoteModal({
  open,
  onOpenChange,
  matterReference,
  noteType: initialNoteType,
  bodyMarkdown,
  sourceContext,
  userName,
  userId,
  getHeaders,
  t,
}) {
  const [matterRef, setMatterRef] = useState(matterReference || "");
  const [title, setTitle] = useState(DEFAULT_TITLES[initialNoteType] || "Note");
  const [noteType, setNoteType] = useState(initialNoteType || "general");
  const [requiresReview, setRequiresReview] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedPath, setSavedPath] = useState(null);
  const [timeBox, setTimeBox] = useState(EMPTY_TIME);

  const handleSave = async () => {
    if (timeFieldProblem(timeBox)) { toast.error(timeFieldProblem(timeBox)); return; }
    setSaving(true);
    try {
      const payload = {
        matter_reference: matterRef,
        note_type: noteType,
        title,
        body_markdown: bodyMarkdown,
        source_context: sourceContext || {},
        created_by_user_id: userId || "",
        created_by_display_name: userName || "",
        created_via: "ai_assistant",
        requires_review: requiresReview,
      };
      const res = await axios.post(`${API}/hub/matter-notes`, payload, { headers: getHeaders() });
      const sp = res.data?.sharepoint_path;
      if (sp) {
        setSavedPath(sp);
        toast.success("Matter note saved and filed to Attendances.");
      } else {
        toast.success("Matter note saved to database.");
      }
      // BM-H4112: the note is saved either way; time is said plainly if refused.
      const t = await recordTouchpointTime({
        value: timeBox, matterRef, source: "matter_note",
        sourceRef: res.data?.id || res.data?.note?.id || null, headers: getHeaders(),
      });
      if (t.recorded) toast.success("Time recorded");
      else if (t.error) toast.error(`Note saved, but time not recorded: ${t.error}`);
    } catch (err) {
      const msg = errorMessage(err, "Failed to save matter note");
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSavedPath(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px]" data-testid="save-matter-note-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-400" />
            Save as matter note
          </DialogTitle>
        </DialogHeader>

        {savedPath ? (
          <div className="space-y-4 py-2">
            <p className="text-sm text-status-green font-medium" data-testid="matter-note-saved-msg">Note saved and filed to Attendances.</p>
            <a
              href={savedPath}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-blue-400 hover:text-blue-300 underline"
              data-testid="matter-note-sp-link"
            >
              <ExternalLink className="w-4 h-4" />
              Open in SharePoint
            </a>
            <DialogFooter>
              <Button variant="outline" onClick={handleClose} data-testid="matter-note-close-btn">Close</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <SmartClientMatterSearch
                label="Matter"
                placeholder="Search matter or enter reference..."
                initialValue={matterRef}
                onSelect={(result) => {
                  setMatterRef(result.matter_reference || result.label || "");
                }}
                darkMode={false}
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground" htmlFor="note-title">Title</label>
              <Input
                id="note-title"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Note title"
                data-testid="matter-note-title-input"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground" htmlFor="note-type">Note type</label>
              <select
                id="note-type"
                value={noteType}
                onChange={e => setNoteType(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="matter-note-type-select"
              >
                {NOTE_TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="requires-review"
                checked={requiresReview}
                onCheckedChange={setRequiresReview}
                data-testid="matter-note-review-checkbox"
              />
              <label htmlFor="requires-review" className="text-sm cursor-pointer">Requires review</label>
            </div>

            <RecordTimeField
              value={timeBox}
              onChange={setTimeBox}
              defaultNarrative={TIME_NARRATIVES[noteType] || "Attendance on file"}
              testIdPrefix="matter-note-time"
              light
            />

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleClose} data-testid="matter-note-cancel-btn">Cancel</Button>
              <Button onClick={handleSave} disabled={saving || !title.trim() || !matterRef.trim()} data-testid="matter-note-save-btn">
                {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-1" />Saving...</> : "Save note"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
